<template>
  <div
    @click="selectOption(option.memTypeCode, option.memStatusCode)"
    class="membership-card-wrapper"
  >
    <div class="membership-card">
      <template v-if="isLarge && screenSize === 'desktop'">
        <div class="membership-card-col-1">
          <div class="membership-card-icon">
            <template v-if="option.icon !== ''">
              <img :src="option.icon" :alt="option.title" />
              <button class="membership-card-icon-btn btn btn-primary">
                Select
              </button>
            </template>
            <template v-else>
              <ApprenticeIcon v-if="option.memStatusCode === 'APR'" />
              <AssistantIcon v-if="option.memStatusCode === 'RAS'" />
              <InternationalIcon v-if="option.memStatusCode === 'OVER'" />
              <RadiographerIcon v-if="option.memStatusCode === 'ORD'" />
              <RetiredIcon v-if="option.memStatusCode === 'RET'" />
              <StudentIcon v-if="option.memStatusCode === 'STU'" />
            </template>
          </div>
        </div>
        <div class="membership-card-col-2">
          <h3>{{ option.title }}</h3>
          <div class="membership-card-description">
            {{ option.description }}
          </div>
          <div class="membership-card-price">£{{ price }}</div>
          <span class="membership-card-price-display">
            {{ priceDisplay }} {{ currentMembershipYear }}
          </span>
          <button class="membership-card-btn btn btn-primary">Select</button>
        </div>
      </template>
      <template v-else>
        <div class="membership-card-icon">
          <template v-if="option.icon !== ''">
            <img :src="option.icon" :alt="option.title" />
          </template>
          <template v-else>
            <ApprenticeIcon v-if="option.memStatusCode === 'APR'" />
            <AssistantIcon v-if="option.memStatusCode === 'RAS'" />
            <InternationalIcon v-if="option.memStatusCode === 'OVER'" />
            <RadiographerIcon v-if="option.memStatusCode === 'ORD'" />
            <RetiredIcon v-if="option.memStatusCode === 'RET'" />
            <StudentIcon v-if="option.memStatusCode === 'STU'" />
          </template>
        </div>
        <h3>{{ option.title }}</h3>
        <div class="membership-card-description">{{ option.description }}</div>
        <div class="membership-card-price">£{{ price }}</div>
        <span class="membership-card-price-display">
          {{ priceDisplay }} {{ currentMembershipYear }}
        </span>
        <button class="membership-card-btn btn btn-primary">Select</button>
      </template>
    </div>
    <div
      v-if="
        option.memStatusCode === 'ORD' &&
          option.message !== '' &&
          option.additionalAnnualRate > 0 &&
          option.additionalMonthlyRate > 0
      "
      class="membership-card-info"
    >
      <div v-html="option.message" />
      <h4>£{{ additionalPrice }}</h4>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../styles/global-variables.scss";

.membership-card-wrapper {
  &:first-child {
    @media all and (min-width: $large-tablet) {
      .membership-card {
        display: grid;
        grid-template-columns: repeat(12, minmax(0, 1fr));
        gap: 1em 1.25em;
        height: auto;

        &-col-1 {
          grid-column: span 4 / span 4;
        }
        &-col-2 {
          grid-column: span 8 / span 8;
          text-align: left;
        }

        &-icon {
          &-btn {
            display: block;
            margin: {
              left: auto;
              right: auto;
            }
          }
        }

        &-btn {
          display: none;
        }
      }
    }
  }
}

.membership-card {
  align-items: stretch;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.13);
  box-sizing: border-box;
  border: 1px solid #d3d3d3;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 24px;
  text-align: center;
  position: relative;
  z-index: 2;
  height: 100%;

  &:hover {
    box-shadow: 0px 8px 6px rgba(0, 0, 0, 0.15);

    .membership-card-radio > div:after {
      opacity: 1;
    }
  }

  h3 {
    font-size: 18px;
    font-weight: 700;
    line-height: 36px;
    margin: 0 0 18px;
    align-self: normal;
  }

  &-icon {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    height: 105px;
    margin: {
      bottom: 16px;
      left: auto;
      right: auto;
    }

    img {
      object-fit: contain;
      object-position: center;
      width: auto;
      height: 100%;
    }

    &-btn {
      display: none;
    }
  }

  &-description {
    font-size: 1em;
    line-height: 1.5;
    margin-bottom: 1.125em;
  }

  &-price {
    font-size: 20px;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 16px;
  }

  > span {
    color: #000000;
    display: block;
    font-size: 16px;
    line-height: 24.5px;
  }

  .btn {
    background-color: var(--button-colour);
    color: #ffffff;
    text-align: center;
    margin-top: 1em;
    align-self: center;
    min-width: 72px;
  }
}
</style>

<style>
.membership-card-info {
  position: relative;
  padding: 1.5em;
  background-color: var(--primary);
  z-index: 1;
  margin-top: -0.5em;
  border-radius: 4px;
  box-shadow: 0px 3px 4px 0px rgba(0, 0, 0, 0.13);
  color: #ffffff;

  > * {
    color: #ffffff;
  }

  h4 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: normal;
    color: #fff;

    & + * {
      margin-top: 8px;
    }
  }

  p {
    line-height: 150%;
    color: #fff;

    & + * {
      margin-top: 1em;
    }
  }
}
</style>

<script>
import { registrationConfig } from "@/helpers/config";
import { useMembershipTypes } from "@/helpers/membership";
import { computed, onMounted, onUnmounted, ref } from "@vue/composition-api";
import ApprenticeIcon from "../assets/icons/membership-types/apprentice.svg";
import AssistantIcon from "../assets/icons/membership-types/assistant.svg";
import InternationalIcon from "../assets/icons/membership-types/international.svg";
import RadiographerIcon from "../assets/icons/membership-types/qual-radiographer.svg";
import RetiredIcon from "../assets/icons/membership-types/retired.svg";
import StudentIcon from "../assets/icons/membership-types/student.svg";

export default {
  components: {
    ApprenticeIcon,
    AssistantIcon,
    InternationalIcon,
    RadiographerIcon,
    RetiredIcon,
    StudentIcon,
  },
  props: ["option", "priceDisplay", "isLarge"],
  setup(props, { root }) {
    const { currentMembershipYear } = registrationConfig;
    const { setMembershipData } = useMembershipTypes();

    const price = computed(() => {
      switch (props.priceDisplay) {
        case "Annually":
          return props.option.annualRate.toFixed(2);
        case "Monthly":
          return props.option.monthlyRate.toFixed(2);
      }
    });

    const additionalPrice = computed(() => {
      switch (props.priceDisplay) {
        case "Annually":
          return props.option.additionalAnnualRate.toFixed(2);
        case "Monthly":
          return props.option.additionalMonthlyRate.toFixed(2);
      }
    });

    const selectOption = (type, status) => {
      setMembershipData(type, status);
      root.$router.push({ path: "/register/membership" });
    };

    const screenWidth = ref(window.innerWidth);

    const updateScreenWidth = () => {
      screenWidth.value = window.innerWidth;
    };

    // Listen for window resize
    onMounted(() => {
      window.addEventListener("resize", updateScreenWidth);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", updateScreenWidth);
    });

    const screenSize = computed(() => {
      if (screenWidth.value < 640) return "mobile";
      if (screenWidth.value < 1024) return "tablet";
      return "desktop";
    });

    return {
      currentMembershipYear,
      price,
      selectOption,
      screenSize,
      additionalPrice,
    };
  },
};
</script>
